import type { GlobalModal, Modal } from '~/types'
import type { GlobalComponents } from 'vue'

/**
 * This composable provides methods to interact with the global modal queue.
 *
 * ### Usage
 * - **Local modals:** Use the computed property returned by `createVisibilityComputed()` directly within a modal's `v-model`.
 *   This allows you to have individual modal visibility locally, the modal queue will be managed automatically.
 *
 * - **Global modals:** Use `queueModal()` to add a modal to the global queue, optionally with priority, for globally managed modal stacking and display.
 *
 * ### Local Modal Example
 * ```html
 * <template>
 *   <TModalCritical v-model:visible="isVisible">
 *     <!-- Modal content here -->
 *   </TModalCritical>
 * </template>
 * <script setup lang="ts">
 * const { createVisibilityComputed } = useModals()
 * const isVisible = createVisibilityComputed()
 * </script>
 * ```
 *
 * ### Global Modal Example
 * ```html
 * <script setup lang="ts">
 * const { queueModal } = useModals()
 * queueModal({ globalModal: { tag: GLOBAL_MODAL_COMPONENTS.AppDownloadAppModalCritical }})
 * </script>
 * ```
 */
export default function useModals() {
  const modalsQueue = useModalsQueue()

  const currentModalID = computed(() => modalsQueue.value.queue[modalsQueue.value.index]?.id)
  const nextModal = computed(() => modalsQueue.value.queue[modalsQueue.value.index + 1])

  /**
   * Loads a modal component dynamically.
   * @param componentName - The name of the component to load.
   * @returns A promise that resolves to the component.
   */
  function loadModalComponent(componentName: string) {
    const componentMap: Record<string, () => Promise<{ default: GlobalComponents[keyof GlobalComponents] }>> = {
      AppDownloadAppModalCritical: () => import('~/components/app/download/download-app-modal-critical.vue'),
      AppDownloadAppModalSubtle: () => import('~/components/app/download/download-app-modal-subtle.vue'),
      BookingEditChangeRequestErrorModal: () => import('~/components/booking/edit/change-request-error-modal.vue'),
      BookingPaymentDetailsModal: () => import('~/components/booking/payment-details-modal/payment-details-modal.vue'),
      BookingPayoutDetailsModal: () => import('~/components/booking/payout-details-modal/payout-details-modal.vue'),
      BookingReviewsReportModal: () => import('~/components/booking/reviews/report-modal.vue'),
      BookingUpsellModal: () => import('~/components/booking/upsell-modal.vue'),
      ModalsBookCallModal: () => import('~/components/modals/book-call-modal.vue'),
    }

    const loader = componentMap[componentName]
    if (!loader) {
      throw new Error(`Modal component '${componentName}' not found in component registry`)
    }

    return loader()
  }

  /**
   *
   * @param hasPriority - If true, the modal will be added to the front of the queue
   * @param modalID - The ID of the modal to be added to the queue
   * @returns WritableComputedRef<boolean, boolean> to be used in the modal's v-model
   */
  function createVisibilityComputed({ hasPriority, modalID }: { hasPriority?: boolean, modalID?: string } = {}) {
    const id = modalID ?? generateUuid()
    return computed({
      get: () => id === currentModalID.value,
      set: (value) => {
        if (value) {
          queueModal({ hasPriority, modalID: id })
        }
        else {
          dequeueModal(id)
        }
      },
    })
  }

  /**
   *
   * @param hasPriority - If true, the modal will be added to the front of the queue
   * @param modalID - The ID of the modal to be added to the queue
   * @param globalModal - The global modal object to be added to the queue
   */
  function queueModal({ hasPriority, modalID, globalModal }: { hasPriority?: boolean, modalID?: string, globalModal?: GlobalModal }) {
    const modal = {
      id: modalID ?? generateUuid(),
      ...globalModal ?? {},
    }

    if (hasPriority) {
      addPriorityModalToQueue([modal].flat())
    }
    else {
      addModalToQueue([modal].flat())
    }

    dequeueDuplicateModals(modal.id)

    if (!modalsQueue.value.isVisible) {
      showNextModal()
    }
  }

  function showNextModal() {
    if (!nextModal.value) {
      hideModal()
      clearQueue()
      return
    }

    moveForward()
    showModal()
  }

  function dequeueModal(modalID: string) {
    if (currentModalID.value === modalID) {
      moveBackward()
    }
    modalsQueue.value.queue = modalsQueue.value.queue.filter((m) => m.id !== modalID)

    showNextModal()
  }

  function dequeueDuplicateModals(modalID: string) {
    const firstIndex = modalsQueue.value.queue.findIndex((m) => m.id === modalID)
    modalsQueue.value.queue = modalsQueue.value.queue.filter((item, pos) => {
      return item.id !== modalID || pos === firstIndex
    })
  }

  function navigationPurge() {
    purge()
    showNextModal()
  }

  function moveForward() {
    modalsQueue.value.index++
  }

  function moveBackward() {
    modalsQueue.value.index--
  }

  function addPriorityModalToQueue(modals: Modal[]) {
    modalsQueue.value.queue.unshift(...modals)
  }

  function addModalToQueue(modals: Modal[]) {
    modalsQueue.value.queue.push(...modals)
  }

  function showModal() {
    modalsQueue.value.isVisible = true
  }

  function hideModal() {
    modalsQueue.value.isVisible = false
  }

  function purge() {
    if (!modalsQueue.value?.queue) {
      return
    }

    modalsQueue.value.queue = modalsQueue.value.queue.filter((modal) => typeof modal === 'object' && modal.persist)
    modalsQueue.value.index = -1
    modalsQueue.value.isVisible = false
  }

  function clearQueue() {
    modalsQueue.value.queue = []
    modalsQueue.value.index = -1
  }

  return {
    modalsQueue,

    queueModal,
    createVisibilityComputed,
    showNextModal,
    navigationPurge,
    loadModalComponent,
  }
}
